import React from 'react';
import { Users } from 'lucide-react';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { InfluencerRow, InfluencerGridCard } from '../InfluencerTableComponents';
import { getTierColor } from '../constants';
import type { Influencer, InfluencerViewMode } from '../types';

export interface InfluencerContentAreaProps {
  isLoading: boolean;
  error: Error | null;
  viewMode: InfluencerViewMode;
  influencers: Influencer[];
  selectedInfluencers: string[] | undefined;
  isAllSelected: boolean;
  onSelectAll: () => void;
  onSelectInfluencer: (id: string) => void;
  onRefreshInfluencer: (id: string) => void;
  refreshingInfluencerId: string | null;
}

export const InfluencerContentArea: React.FC<InfluencerContentAreaProps> = ({
  isLoading,
  error,
  viewMode,
  influencers,
  selectedInfluencers = [],
  isAllSelected,
  onSelectAll,
  onSelectInfluencer,
  onRefreshInfluencer,
  refreshingInfluencerId,
}) => {
  return (
    <div className="flex-1 overflow-auto p-6">
      {isLoading ? (
        <div className="text-center py-24 text-gray-500">Loading influencers...</div>
      ) : error ? (
        <div className="text-center py-24 text-red-600">
          Error loading influencers: {error.message}
        </div>
      ) : viewMode === 'table' ? (
        <div className="bg-white rounded-lg border border-gray-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={onSelectAll}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                </TableHead>
                <TableHead>
                  <div className="flex items-center space-x-1">
                    <span>Influencer</span>
                    {selectedInfluencers.length > 0 && (
                      <span className="text-purple-600 font-semibold">
                        ({selectedInfluencers.length})
                      </span>
                    )}
                  </div>
                </TableHead>
                <TableHead className="text-center">Followers</TableHead>
                <TableHead className="text-center">Engagement</TableHead>
                <TableHead className="text-center">Average Views</TableHead>
                <TableHead className="text-center">Category</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {influencers.map((influencer) => (
                <InfluencerRow
                  key={influencer.id}
                  influencer={influencer}
                  selectedInfluencers={selectedInfluencers}
                  handleSelectInfluencer={onSelectInfluencer}
                  onRefreshInfluencer={onRefreshInfluencer}
                  isRefreshing={refreshingInfluencerId === influencer.id}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {influencers.map((influencer) => (
            <InfluencerGridCard
              key={influencer.id}
              influencer={influencer}
              selectedInfluencers={selectedInfluencers}
              handleSelectInfluencer={onSelectInfluencer}
              getTierColor={getTierColor}
            />
          ))}
        </div>
      )}

      {influencers.length === 0 && !isLoading && !error && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Users className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No influencers found</h3>
          <p className="text-gray-500">Try adjusting your search or filters to find influencers.</p>
        </div>
      )}
    </div>
  );
};
